import React, { useState, useRef, useCallback } from 'react';
import './DocumentUpload.css';

const ALLOWED_TYPES = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
};
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_FILE_SIZE_LABEL = '10MB';

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(name) {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.substring(idx).toLowerCase() : '';
}

function validateFile(file, existingFiles) {
  const errors = [];

  // Check file type by MIME and extension
  const ext = getFileExtension(file.name);
  const mimeAllowed = Object.keys(ALLOWED_TYPES).includes(file.type);
  const extAllowed = ALLOWED_EXTENSIONS.includes(ext);

  if (!mimeAllowed && !extAllowed) {
    errors.push(`"${file.name}" is not a supported file type. Please upload PDF, JPG, or PNG files only.`);
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    errors.push(`"${file.name}" is ${formatFileSize(file.size)} which exceeds the ${MAX_FILE_SIZE_LABEL} limit.`);
  }

  if (file.size === 0) {
    errors.push(`"${file.name}" is empty and cannot be uploaded.`);
  }

  // Check duplicate names
  if (existingFiles.some((f) => f.name === file.name)) {
    errors.push(`"${file.name}" has already been added.`);
  }

  return errors;
}

export default function DocumentUpload({ files, onChange, disabled = false }) {
  const [dragActive, setDragActive] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const clearFeedback = useCallback(() => {
    setFeedback([]);
  }, []);

  const processFiles = useCallback(
    (incomingFiles) => {
      if (disabled) return;

      const newFeedback = [];
      const accepted = [];

      // Check total file count
      const remainingSlots = MAX_FILES - files.length;
      if (remainingSlots <= 0) {
        newFeedback.push({
          type: 'error',
          message: `You can upload a maximum of ${MAX_FILES} files. Please remove a file before adding more.`,
        });
        setFeedback(newFeedback);
        return;
      }

      const filesToProcess = Array.from(incomingFiles).slice(0, remainingSlots);
      const skipped = incomingFiles.length - filesToProcess.length;

      for (const file of filesToProcess) {
        const errors = validateFile(file, [...files, ...accepted]);
        if (errors.length > 0) {
          errors.forEach((msg) => newFeedback.push({ type: 'error', message: msg }));
        } else {
          accepted.push(file);
        }
      }

      if (skipped > 0) {
        newFeedback.push({
          type: 'warning',
          message: `${skipped} file(s) were not added because the maximum of ${MAX_FILES} files would be exceeded.`,
        });
      }

      if (accepted.length > 0) {
        newFeedback.push({
          type: 'success',
          message: `${accepted.length} file(s) added successfully.`,
        });
        onChange([...files, ...accepted]);
      }

      setFeedback(newFeedback);
    },
    [files, onChange, disabled]
  );

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragCounter.current += 1;
      if (dragCounter.current === 1) {
        setDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      dragCounter.current = 0;
      if (disabled) return;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(Array.from(e.dataTransfer.files));
        e.dataTransfer.clearData();
      }
    },
    [processFiles, disabled]
  );

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      // Reset input so same file can be re-selected
      e.target.value = '';
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback(
    (index) => {
      if (disabled) return;
      const updated = files.filter((_, i) => i !== index);
      onChange(updated);
      setFeedback([{ type: 'success', message: 'File removed.' }]);
    },
    [files, onChange, disabled]
  );

  const handleBrowseClick = useCallback(() => {
    if (disabled) return;
    fileInputRef.current?.click();
  }, [disabled]);

  const atLimit = files.length >= MAX_FILES;

  return (
    <div className="document-upload" data-testid="document-upload">
      {/* Drop zone */}
      <div
        className={`drop-zone ${dragActive ? 'drop-zone--active' : ''} ${atLimit ? 'drop-zone--disabled' : ''} ${disabled ? 'drop-zone--disabled' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!atLimit && !disabled ? handleBrowseClick : undefined}
        role="button"
        tabIndex={atLimit || disabled ? -1 : 0}
        aria-label="Upload documents. Drag and drop files here or click to browse."
        aria-disabled={atLimit || disabled}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !atLimit && !disabled) {
            e.preventDefault();
            handleBrowseClick();
          }
        }}
      >
        <div className="drop-zone__icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M24 4L14 16H20V28H28V16H34L24 4Z"
              fill="currentColor"
              opacity="0.6"
            />
            <path
              d="M40 32V40H8V32H4V40C4 42.2 5.8 44 8 44H40C42.2 44 44 42.2 44 40V32H40Z"
              fill="currentColor"
              opacity="0.6"
            />
          </svg>
        </div>
        <p className="drop-zone__text">
          {atLimit
            ? `Maximum ${MAX_FILES} files reached`
            : dragActive
              ? 'Drop your files here'
              : 'Drag & drop files here or tap to browse'}
        </p>
        <p className="drop-zone__hint">
          PDF, JPG, PNG &bull; Max {MAX_FILE_SIZE_LABEL} per file &bull; Up to {MAX_FILES} files
        </p>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="drop-zone__input"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        multiple
        onChange={handleFileInput}
        data-testid="file-input"
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Feedback messages */}
      {feedback.length > 0 && (
        <div className="upload-feedback" role="alert" aria-live="polite" data-testid="upload-feedback">
          {feedback.map((fb, i) => (
            <div key={i} className={`upload-feedback__item upload-feedback__item--${fb.type}`}>
              <span className="upload-feedback__icon" aria-hidden="true">
                {fb.type === 'error' ? '✕' : fb.type === 'warning' ? '⚠' : '✓'}
              </span>
              <span>{fb.message}</span>
            </div>
          ))}
          <button
            className="upload-feedback__dismiss"
            onClick={clearFeedback}
            aria-label="Dismiss messages"
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="file-list" data-testid="file-list">
          <p className="file-list__count">
            {files.length} of {MAX_FILES} files attached
          </p>
          <ul className="file-list__items">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="file-list__item" data-testid="file-list-item">
                <div className="file-list__item-icon" aria-hidden="true">
                  {getFileExtension(file.name) === '.pdf' ? '📄' : '🖼️'}
                </div>
                <div className="file-list__item-info">
                  <span className="file-list__item-name" title={file.name}>
                    {file.name}
                  </span>
                  <span className="file-list__item-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  className="file-list__item-remove"
                  onClick={() => handleRemoveFile(index)}
                  aria-label={`Remove ${file.name}`}
                  type="button"
                  disabled={disabled}
                  data-testid={`remove-file-${index}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
